import React from 'react';
import { useIntl } from 'react-intl';

import {
  Status,
  StyledTemperatureGauge,
  StyledTemperatureGaugeWrapper,
  TempNumber,
  TempType,
  InfoWrapper,
  GaugeTemp,
  OfflineMessage,
  InfoWrapperContent,
  InfoWrapperContentText,
  InfoWrapperTemperature,
  InfoWrapperCloseButton,
  DualInfoWrapper,
  InfoWrapperStatus,
  InfoWrapperFan,
} from 'pages/thermostat/temperature-gauge/styles';
import { ThermostatData } from 'types/thermostatData';

import { TemperatureSliderProps } from '../../../types/thermostatThemperatureSliderProps';
import { Remove } from '../../../components/icons/x-remove';
import { ThermostatPageSettings } from '../../../types/thermostatPageSettings';

export interface TemperatureGaugeProps {
  desiredTemperature: number;
  currentTemperature: number;
  desiredCoolTemperature: number;
  desiredHeatTemperature: number;
  selectedThermostat: ThermostatData;
  hvacMode: TemperatureSliderProps['type'];
  hold: ThermostatPageSettings['hold'];
  onResumeProgram: () => void;
}

export const TemperatureGauge = ({
  currentTemperature,
  desiredTemperature,
  desiredHeatTemperature,
  desiredCoolTemperature,
  selectedThermostat,
  hvacMode,
  hold,
  onResumeProgram,
}: TemperatureGaugeProps) => {
  const intl = useIntl();
  const offline = selectedThermostat?.runtime?.connected === false;

  /**
   * The unit the reader expects. Fahrenheit was hardcoded in two places — a JSX literal and a CSS
   * `content` string — so a German or French tenant read their temperature labelled °F, in an
   * otherwise fully translated UI. No catalogue key was involved, so no parity gate could see it.
   *
   * The Ecobee account setting wins where we have it. Where we do not, the locale decides: Fahrenheit
   * is the United States and essentially nowhere else in the set this app ships, so defaulting the
   * rest of the world to °F is the wrong way round.
   */
  const useCelsius = selectedThermostat?.settings?.useCelsius
    ?? !['en', 'en-us'].includes(intl.locale.toLowerCase());
  const unit = useCelsius ? '°C' : '°F';
  /* eslint no-confusing-arrow: ["off", {"allowParens": false}] */
  const renderDualInfoWrapper = () => hold && hold.endTime ? (
    <InfoWrapper>
      <InfoWrapperContent>
        <InfoWrapperContentText key={hvacMode} mode={hvacMode}>
          <InfoWrapperTemperature key={hvacMode} mode="heat" unit={unit}>
            {desiredHeatTemperature}
          </InfoWrapperTemperature>
          -&nbsp;
          {desiredCoolTemperature}
            &nbsp;
          {/* The time is an argument, not a value after the word "until" — Hungarian suffixes it. */}
          {intl.formatMessage({ id: 'tile.holdUntil' }, { time: hold.endTime })}
            &nbsp;
          <InfoWrapperCloseButton onClick={onResumeProgram}>
            <Remove />
          </InfoWrapperCloseButton>
        </InfoWrapperContentText>
      </InfoWrapperContent>
    </InfoWrapper>
  ) : (
    <DualInfoWrapper>
      <InfoWrapper dual>
        <InfoWrapperContent>
          <InfoWrapperContentText mode="heat">
            <InfoWrapperTemperature mode="heat" unit={unit}>{desiredHeatTemperature}</InfoWrapperTemperature>
          </InfoWrapperContentText>
        </InfoWrapperContent>
      </InfoWrapper>
      <InfoWrapper dual>
        <InfoWrapperContent>
          <InfoWrapperContentText mode="cool">
            <InfoWrapperTemperature mode="cool" unit={unit}>{desiredCoolTemperature}</InfoWrapperTemperature>
          </InfoWrapperContentText>
        </InfoWrapperContent>
      </InfoWrapper>
    </DualInfoWrapper>
  );

  const renderInfoWrapper = () => (
    <InfoWrapper>
      <InfoWrapperContent>
        <InfoWrapperContentText key={hvacMode} mode={hvacMode}>
          {hold && hold?.endTime ? (
            <>
              <InfoWrapperTemperature key={hvacMode} mode={hvacMode} unit={unit}>
                {desiredTemperature}
              </InfoWrapperTemperature>
              &nbsp;
              {intl.formatMessage({ id: 'tile.holdUntil' }, { time: hold.endTime })}
              &nbsp;
              <InfoWrapperCloseButton onClick={onResumeProgram}>
                <Remove />
              </InfoWrapperCloseButton>
            </>
          ) : (
            <InfoWrapperTemperature key={hvacMode} mode={hvacMode} unit={unit}>
              {desiredTemperature}
            </InfoWrapperTemperature>
          )}
        </InfoWrapperContentText>
      </InfoWrapperContent>
    </InfoWrapper>
  );
  const renderHoldStatusWrapper = () => (
    <InfoWrapper>
      <InfoWrapperContent>
        <InfoWrapperContentText mode="off">
          <InfoWrapperStatus>{hold.holdClimateRef}</InfoWrapperStatus>
          <InfoWrapperCloseButton onClick={onResumeProgram}>
            <Remove />
          </InfoWrapperCloseButton>
        </InfoWrapperContentText>
      </InfoWrapperContent>
    </InfoWrapper>
  );

  const renderFanInfoWrapper = () => (
    <InfoWrapper>
      <InfoWrapperContent>
        <InfoWrapperContentText mode="off">
          <InfoWrapperFan>
            {intl.formatMessage({ id: 'tile.fanUntil' }, { time: hold.endTime })}
          </InfoWrapperFan>
          <InfoWrapperCloseButton onClick={onResumeProgram}>
            <Remove />
          </InfoWrapperCloseButton>
        </InfoWrapperContentText>
      </InfoWrapperContent>
    </InfoWrapper>
  );

  const renderContent = () => {
    if (hold?.holdClimateRef && hold.holdClimateRef !== '') {
      return renderHoldStatusWrapper();
    }
    if (hold && hold.fan === 'on' && hold.isTemperatureAbsolute === false) {
      return renderFanInfoWrapper();
    }
    if (hvacMode === 'auto') {
      return renderDualInfoWrapper();
    }
    if (['heat', 'cool'].includes(hvacMode)) {
      return renderInfoWrapper();
    }
    return null;
  };

  return (
    <StyledTemperatureGaugeWrapper>
      <StyledTemperatureGauge role="presentation" aria-label="temperature-gauge" data-testid="temperature-gauge">
        {offline ? (
          <OfflineMessage role="article" aria-label="offline-message" data-testid="offline-message">
            {intl.formatMessage({ id: 'offline_termostat' })}
          </OfflineMessage>
        ) : (
          <>
            <Status>{intl.formatMessage({ id: 'temperature' })}</Status>
            <GaugeTemp>
              <TempNumber role="article" aria-label="temperature-number">
                {currentTemperature}
              </TempNumber>
              <TempType>{unit}</TempType>
            </GaugeTemp>
            {renderContent()}
          </>
        )}
      </StyledTemperatureGauge>
    </StyledTemperatureGaugeWrapper>
  );
};
